"use client";

import { c as _c } from "react/compiler-runtime";
import { DecisionMetadata, outputId, recordOrigin, ReferralWorkflowTimeline, stepStatus, type ReferralWorkflowStep } from "@/configuration/groups/confluent-all-star/components/referral-timeline";
import type { ConfluentCarePlanTaskFields } from "@/configuration/groups/confluent-all-star/workflows/care-plan-input";
import { formatUSPhoneNumber } from "@/shared/utils/phone-utils";
import { FieldLayout, type Field } from "@/src/components/cards/common";
import PatientCard from "@/src/components/cards/patient-card/patient-card";
import { CopyableContent } from "@/src/components/copyable-content";
import { OutboundFaxPdfPreview, outboundFaxPdfPath } from "@/src/components/tasks/document-workflow-steps";
import { WorkflowStepInformationNeeded } from "@/src/components/tasks/workflow-step-information-needed";
import { WorkflowStepResumeButton } from "@/src/components/tasks/workflow-step-resume-button";
import { PlanOfCareAction } from "@/src/lib/ehr/clinicient/plan-of-care";
import { formatDateMmDdYyyy } from "@/src/lib/utils/date-and-phone-utils";
import { CARE_PLAN_FAX_NOTE_ID } from "@/src/lib/workflows/steps/ehr/clinicient-chart-note-ids";
import { StepKey } from "@/src/lib/workflows/steps/types";
import { FileType } from "@/src/models/file";
import { TaskStatus, type Task } from "@/src/models/task";
import type { TaskApproval } from "@/src/models/task-approval";
import { TaskStepStatus, type TaskStep } from "@/src/models/task-step";
const FAX_STEP_KEYS = [StepKey.RESOLVE_REPLY_FAX, StepKey.BUILD_REPLY_FAX, StepKey.SEND_REPLY_FAX] as const;
const UNSIGNED_CARE_PLAN_STEP_KEYS = [...FAX_STEP_KEYS, StepKey.ENSURE_PATIENT, StepKey.CLINICIENT_ENSURE_CASE, StepKey.CLINICIENT_PATIENT_NOTE, StepKey.CLINICIENT_CASE_TASK] as const;
const SIGNED_CARE_PLAN_STEP_KEYS = [StepKey.REQUIRE_PATIENT_LOOKUP, ...FAX_STEP_KEYS, StepKey.ENSURE_PATIENT, StepKey.CLINICIENT_ENSURE_CASE, StepKey.CLINICIENT_ENSURE_PLAN_OF_CARE, StepKey.CLINICIENT_PLAN_OF_CARE_DOCUMENT_UPLOAD, StepKey.CLINICIENT_CASE_DOCUMENT_UPLOAD, StepKey.CLINICIENT_CASE_TASK] as const;
type CarePlanStepKey = (typeof UNSIGNED_CARE_PLAN_STEP_KEYS)[number] | (typeof SIGNED_CARE_PLAN_STEP_KEYS)[number];
const PLAN_OF_CARE_ACTION_LABEL: Record<PlanOfCareAction, string> = {
  [PlanOfCareAction.ATTACH_ONLY]: "Existing entry with the same approval date",
  [PlanOfCareAction.CREATE]: "New entry created",
  [PlanOfCareAction.SKIP]: "Skipped: an entry with the same approval date already has a document",
  [PlanOfCareAction.UPDATE]: "Existing entry's approval date updated"
};
function planOfCareActionLabel(value: unknown): string {
  const action = Object.values(PlanOfCareAction).find(candidate => candidate === value);
  return action ? PLAN_OF_CARE_ACTION_LABEL[action] : "Not recorded";
}
function writtenNote(step: TaskStep | undefined): {
  noteEhrId: string;
  note: string;
} | null {
  const notes = step?.output?.notes;
  if (!Array.isArray(notes)) return null;
  const match = notes.find(note => note?.id === CARE_PLAN_FAX_NOTE_ID);
  return typeof match?.noteEhrId === "string" && typeof match?.note === "string" ? match : null;
}
export function ConfluentCarePlanSteps(t0) {
  const $ = _c(58);
  const {
    task,
    taskSteps,
    pendingApproval,
    signed,
    dateOfEvent,
    noteText,
    caseRecordDescription,
    taskFields
  } = t0;
  const stepKeys = signed ? SIGNED_CARE_PLAN_STEP_KEYS : UNSIGNED_CARE_PLAN_STEP_KEYS;
  let hasInformationNeededStep;
  let inboundNote;
  let isCompleted;
  let needsAttention;
  let steps;
  let workflowContinuing;
  if ($[0] !== caseRecordDescription || $[1] !== dateOfEvent || $[2] !== noteText || $[3] !== pendingApproval || $[4] !== signed || $[5] !== stepKeys || $[6] !== task || $[7] !== taskFields || $[8] !== taskSteps) {
    const orderedTaskSteps = taskSteps.toSorted(_temp);
    const findStep = key => orderedTaskSteps.find(step => step.stepKey === key);
    const lookupStep = findStep(StepKey.REQUIRE_PATIENT_LOOKUP);
    const routingStep = findStep(StepKey.RESOLVE_REPLY_FAX);
    const buildStep = findStep(StepKey.BUILD_REPLY_FAX);
    const sendStep = findStep(StepKey.SEND_REPLY_FAX);
    const patientStep = findStep(StepKey.ENSURE_PATIENT);
    const caseStep = findStep(StepKey.CLINICIENT_ENSURE_CASE);
    const noteStep = findStep(StepKey.CLINICIENT_PATIENT_NOTE);
    const planOfCareStep = findStep(StepKey.CLINICIENT_ENSURE_PLAN_OF_CARE);
    const planOfCareDocumentStep = findStep(StepKey.CLINICIENT_PLAN_OF_CARE_DOCUMENT_UPLOAD);
    const caseRecordStep = findStep(StepKey.CLINICIENT_CASE_DOCUMENT_UPLOAD);
    const taskStep = findStep(StepKey.CLINICIENT_CASE_TASK);
    const isSkipped = _temp2;
    const targetFaxNumber = outputId(routingStep, "targetFaxNumber");
    const pageCount = typeof buildStep?.output?.pageCount === "number" ? buildStep.output.pageCount : null;
    let t1;
    if ($[15] !== task.id || $[16] !== taskSteps) {
      t1 = outboundFaxPdfPath(task.id, taskSteps);
      $[15] = task.id;
      $[16] = taskSteps;
      $[17] = t1;
    } else {
      t1 = $[17];
    }
    const pdfPath = t1;
    const faxId = outputId(sendStep, "faxId");
    const patientId = outputId(patientStep, "patientEhrId");
    const caseId = outputId(caseStep, "patientCaseEhrId");
    const caseName = outputId(caseStep, "caseName");
    const caseActive = caseStep?.output?.caseActive;
    const note = writtenNote(noteStep);
    const clinicientTaskId = outputId(taskStep, "taskEhrId");
    const certificationId = outputId(planOfCareStep, "certificationId");
    const planOfCareDocumentId = outputId(planOfCareDocumentStep, "documentEhrId");
    const caseRecordDocumentId = outputId(caseRecordStep, "documentEhrId");
    const faxedBack = signed && faxId !== null;
    const lastStep = orderedTaskSteps.at(-1);
    const onHold = task.status === TaskStatus.AWAITING_INFORMATION || task.status === TaskStatus.REVIEW_NEEDED;
    const formattedFax = targetFaxNumber ? formatUSPhoneNumber(targetFaxNumber) ?? targetFaxNumber : "Not recorded";
    const faxCopy = signed ? {
      resolveTitle: "Find the sender's fax number",
      resolveSummary: "Only when patient lookup found no Clinicient chart: identify the receiving clinic and the office that sent the plan of care.",
      buildTitle: "Generate the not-our-patient fax",
      buildSummary: "Build the not-our-patient cover sheet and attach the plan of care.",
      sendTitle: "Fax the plan of care back to the sender",
      sendSummary: "Submit the fax to the sending office. The workflow ends after this fax. Fax submission does not confirm delivery."
    } : {
      resolveTitle: "Find the referring provider's fax number",
      resolveSummary: "Identify the receiving clinic and the referring provider office that should sign the plan of care.",
      buildTitle: "Generate the fax",
      buildSummary: "Build the cover sheet and attach the unsigned plan of care.",
      sendTitle: "Fax the plan of care to the referring provider for signature",
      sendSummary: "Submit the fax to the referring provider's office. Fax submission does not confirm delivery."
    };
    let t2;
    if ($[18] !== dateOfEvent) {
      t2 = () => dateOfEvent;
      $[18] = dateOfEvent;
      $[19] = t2;
    } else {
      t2 = $[19];
    }
    const caseFields = [{
      key: "caseName",
      label: "Case Name",
      getValue: () => caseName
    }, {
      key: "dateOfEvent",
      label: "Plan of Care Date of Event",
      getValue: t2
    }];
    const planOfCareFields = [{
      key: "action",
      label: "Plan of Care entry",
      getValue: () => planOfCareActionLabel(planOfCareStep?.output?.action)
    }, {
      key: "effectiveDate",
      label: "Effective Date",
      getValue: () => formatDateMmDdYyyy(outputId(planOfCareStep, "effectiveDate"))
    }, {
      key: "approvalDate",
      label: "Approval Date",
      getValue: () => formatDateMmDdYyyy(outputId(planOfCareStep, "approvalDate"))
    }];
    let t3;
    if ($[20] !== taskFields) {
      t3 = () => taskFields.action;
      $[20] = taskFields;
      $[21] = t3;
    } else {
      t3 = $[21];
    }
    let t4;
    if ($[22] !== taskFields) {
      t4 = () => taskFields.dueDate;
      $[22] = taskFields;
      $[23] = t4;
    } else {
      t4 = $[23];
    }
    let t5;
    if ($[24] !== taskFields) {
      t5 = () => taskFields.priority;
      $[24] = taskFields;
      $[25] = t5;
    } else {
      t5 = $[25];
    }
    let t6;
    if ($[26] !== taskFields) {
      t6 = () => taskFields.role;
      $[26] = taskFields;
      $[27] = t6;
    } else {
      t6 = $[27];
    }
    let t7;
    if ($[28] !== taskFields) {
      t7 = () => taskFields.note;
      $[28] = taskFields;
      $[29] = t7;
    } else {
      t7 = $[29];
    }
    const taskFieldList = [{
      key: "action",
      label: "Action",
      getValue: t3
    }, {
      key: "dueDate",
      label: "Due Date",
      getValue: t4
    }, {
      key: "priority",
      label: "Priority",
      getValue: t5
    }, {
      key: "role",
      label: "Assign to Role",
      getValue: t6
    }, {
      key: "note",
      label: "Note",
      colSpan: 2,
      getValue: t7
    }, {
      key: "case",
      label: "Case",
      getValue: () => caseName
    }];
    let t8;
    if ($[30] !== pdfPath || $[31] !== task.id) {
      t8 = pdfPath && <div className="h-[900px] overflow-hidden rounded-md border"><OutboundFaxPdfPreview taskId={task.id} path={pdfPath} /></div>;
      $[30] = pdfPath;
      $[31] = task.id;
      $[32] = t8;
    } else {
      t8 = $[32];
    }
    let t9;
    if ($[33] !== task.patient) {
      t9 = task.patient && <PatientCard patient={task.patient} />;
      $[33] = task.patient;
      $[34] = t9;
    } else {
      t9 = $[34];
    }
    let t10;
    if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <p className="font-medium">The case is chosen in this order:</p>;
      $[35] = t10;
    } else {
      t10 = $[35];
    }
    let t11;
    if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <div>{t10}<ol className="mt-1 list-decimal space-y-1 pl-5 text-muted-foreground"><li>Active cases are checked first. Inactive cases are checked only when no active case matches.</li><li>If the plan of care lists a diagnosis or body part, use the case that matches it the same way referrals are matched (discipline, body part, and ICD-10 codes).</li><li>Otherwise, use the only case; else the only case with a plan of care start date; else the case whose plan of care start date is closest to the plan of care's date of event.</li><li>If cases are still tied, use the one with the soonest upcoming appointment, then the most recent past appointment.</li><li>Create a "Default Case" only when the patient has no cases at all.</li></ol></div>;
      $[36] = t11;
    } else {
      t11 = $[36];
    }
    let t12;
    if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <p className="font-medium">The entry is chosen in this order (progress-report rows are ignored):</p>;
      $[37] = t12;
    } else {
      t12 = $[37];
    }
    let t13;
    if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = <div>{t12}<ol className="mt-1 list-decimal space-y-1 pl-5 text-muted-foreground"><li>An entry whose approval date matches the signed date and has no document: attach to it (highest Clinicient ID if several). If every such entry already has a document, skip.</li><li>Otherwise, an entry with no document, an effective date within 10 days of the date of event, and a blank, placeholder, or same-as-effective approval date: set its approval date to the signed date. Closest effective date wins, then a blank approval date, then the highest Clinicient ID.</li><li>Otherwise, create an entry: effective date = date of event, approval date = signed date, 90 days.</li></ol></div>;
      $[38] = t13;
    } else {
      t13 = $[38];
    }
    const definitions = {
      [StepKey.REQUIRE_PATIENT_LOOKUP]: {
        title: "Confirm patient lookup is complete",
        status: stepStatus(lookupStep, lookupStep !== undefined),
        summary: "Patient lookup must link a Clinicient chart or confirm the patient is not in Clinicient before the plan of care is routed."
      },
      [StepKey.RESOLVE_REPLY_FAX]: {
        title: faxCopy.resolveTitle,
        status: stepStatus(routingStep, isSkipped(routingStep) || targetFaxNumber !== null, false, isSkipped(routingStep)),
        summary: faxCopy.resolveSummary,
        content: <DecisionMetadata items={[{
          label: "Fax to",
          value: formattedFax
        }]} />
      },
      [StepKey.BUILD_REPLY_FAX]: {
        title: faxCopy.buildTitle,
        status: stepStatus(buildStep, isSkipped(buildStep) || pageCount !== null, false, isSkipped(buildStep)),
        summary: faxCopy.buildSummary,
        content: <div className="space-y-3"><DecisionMetadata items={[{
            label: "Pages",
            value: pageCount ?? "Not recorded"
          }]} />{t8}</div>
      },
      [StepKey.SEND_REPLY_FAX]: {
        title: faxCopy.sendTitle,
        status: stepStatus(sendStep, isSkipped(sendStep) || faxId !== null, false, isSkipped(sendStep)),
        summary: faxCopy.sendSummary,
        content: <DecisionMetadata items={[{
          label: "Fax recipient",
          value: formattedFax
        }, {
          label: "Fax record",
          value: faxId ?? "Not recorded"
        }]} />
      },
      [StepKey.ENSURE_PATIENT]: {
        title: "Confirm patient chart exists in Clinicient with the following demographic information",
        status: stepStatus(patientStep, patientId !== null),
        summary: "The plan of care is only filed to an existing chart; a new chart is never created.",
        content: <div className="space-y-3">{t9}<DecisionMetadata items={[{
            label: "Clinicient patient ID",
            value: patientId ?? "Not recorded"
          }, {
            label: "Patient",
            value: recordOrigin(patientStep?.output?.created)
          }]} /></div>
      },
      [StepKey.CLINICIENT_ENSURE_CASE]: {
        title: "Find the plan of care's case",
        status: stepStatus(caseStep, caseId !== null),
        summary: "Attach the plan of care to the patient's existing Clinicient case. A case is created only when the patient has none; cases that cannot be told apart require review.",
        content: <div className="space-y-4 text-sm">{t11}<FieldLayout hideEmptyFields={false} fields={caseFields} patient={null} /><DecisionMetadata items={[{
            label: "Clinicient case ID",
            value: caseId ?? "Not recorded"
          }, {
            label: "Case",
            value: recordOrigin(caseStep?.output?.created)
          }, ...(typeof caseActive === "boolean" ? [{
            label: "Case status",
            value: caseActive ? "Active" : "Inactive"
          }] : [])]} /></div>
      },
      [StepKey.CLINICIENT_PATIENT_NOTE]: {
        title: "Add plan of care fax note to patient chart",
        status: stepStatus(noteStep, note !== null),
        summary: "To the Notes section of the patient chart, add the following note:",
        content: <div className="space-y-3">{(note?.note ?? noteText) && <div className="rounded-md border bg-muted/30 px-3 py-2"><CopyableContent text={note?.note ?? noteText ?? ""} alwaysShow={true} /></div>}<DecisionMetadata items={[{
            label: "Clinicient note ID",
            value: note?.noteEhrId ?? "Not recorded"
          }]} /></div>
      },
      [StepKey.CLINICIENT_ENSURE_PLAN_OF_CARE]: {
        title: "Add or update the case's Plan of Care entry",
        status: stepStatus(planOfCareStep, certificationId !== null, false, isSkipped(planOfCareStep)),
        summary: "Exactly one Plan of Care entry on the case is created, updated, or attached to.",
        content: <div className="space-y-4 text-sm">{t13}<FieldLayout hideEmptyFields={false} fields={planOfCareFields} patient={null} /><DecisionMetadata items={[{
            label: "Clinicient plan of care ID",
            value: certificationId ?? "Not recorded"
          }]} /></div>
      },
      [StepKey.CLINICIENT_PLAN_OF_CARE_DOCUMENT_UPLOAD]: {
        title: "Attach the signed plan of care to the Plan of Care entry",
        status: stepStatus(planOfCareDocumentStep, isSkipped(planOfCareDocumentStep) || planOfCareDocumentId !== null, false, isSkipped(planOfCareDocumentStep)),
        summary: isSkipped(planOfCareDocumentStep) ? "Not needed: the Plan of Care entry with this approval date already has a document." : "Upload the plan of care to the Plan of Care entry chosen above.",
        content: <DecisionMetadata items={[{
          label: "Clinicient plan of care ID",
          value: certificationId ?? "Not recorded"
        }, {
          label: "Clinicient document ID",
          value: planOfCareDocumentId ?? "Not recorded"
        }]} />
      },
      [StepKey.CLINICIENT_CASE_DOCUMENT_UPLOAD]: {
        title: "Upload the signed plan of care as a case record",
        status: stepStatus(caseRecordStep, caseRecordDocumentId !== null),
        summary: "Upload the plan of care again to the case's attachments as a Case Record.",
        content: <DecisionMetadata items={[{
          label: "Attachment type",
          value: "Case Record"
        }, {
          label: "Description",
          value: caseRecordDescription ?? "Not recorded"
        }, {
          label: "Clinicient document ID",
          value: caseRecordDocumentId ?? "Not recorded"
        }]} />
      },
      [StepKey.CLINICIENT_CASE_TASK]: {
        title: "Create the POC Received task",
        status: stepStatus(taskStep, clinicientTaskId !== null),
        summary: "Create a task related to the plan of care's case, leaving Assign to Staff blank. Clinic, payer, and referrer come from the case; if the case has no clinic, use the clinic for the fax line the plan of care came in on.",
        content: <div className="space-y-3"><FieldLayout hideEmptyFields={false} fields={taskFieldList} patient={null} /><DecisionMetadata items={[{
            label: "Clinicient task ID",
            value: clinicientTaskId ?? "Not recorded"
          }]} /></div>
      }
    };
    let t14;
    if ($[39] !== task.files) {
      t14 = task.files.find(_temp3)?.note;
      $[39] = task.files;
      $[40] = t14;
    } else {
      t14 = $[40];
    }
    inboundNote = t14;
    const informationNeededStep = onHold ? lastStep : undefined;
    hasInformationNeededStep = stepKeys.some(key_0 => key_0 === informationNeededStep?.stepKey);
    steps = stepKeys.map(key_1 => {
      const definition = definitions[key_1];
      const notNeededAfterFax = faxedBack && !FAX_STEP_KEYS.some(faxKey => faxKey === key_1) && !findStep(key_1);
      const step_1 = notNeededAfterFax ? {
        ...definition,
        status: "not_needed"
      } : definition;
      const isBlockingStep = key_1 === lastStep?.stepKey && (onHold || task.status === TaskStatus.FAILED);
      const needsAction = key_1 === informationNeededStep?.stepKey;
      return {
        ...step_1,
        status: step_1.status === "failed" ? "failed" : needsAction ? "needs_action" : step_1.status,
        content: needsAction ? <><WorkflowStepInformationNeeded note={inboundNote} />{step_1.content}</> : step_1.content,
        continuation: isBlockingStep && !pendingApproval && lastStep ? {
          instruction: "After fixing the issue, continue from this step. Completed steps will be preserved.",
          control: <WorkflowStepResumeButton key={`${lastStep.id}:${task.updatedAt}`} task={task} step={lastStep} />
        } : undefined
      };
    });
    const allSynced = steps.every(_temp4);
    isCompleted = task.status === TaskStatus.COMPLETED;
    const isProcessing = task.status === TaskStatus.PROCESSING;
    let t15;
    if ($[41] !== taskSteps) {
      t15 = taskSteps.some(_temp5);
      $[41] = taskSteps;
      $[42] = t15;
    } else {
      t15 = $[42];
    }
    const hasFailedStep = t15;
    let t16;
    if ($[43] !== taskSteps) {
      t16 = taskSteps.some(_temp6);
      $[43] = taskSteps;
      $[44] = t16;
    } else {
      t16 = $[44];
    }
    const hasTimedHold = t16;
    let t17;
    if ($[45] !== taskSteps) {
      t17 = taskSteps.some(_temp7);
      $[45] = taskSteps;
      $[46] = t17;
    } else {
      t17 = $[46];
    }
    const hasAsyncStep = t17;
    const workflowNeedsAttention = onHold || hasFailedStep;
    workflowContinuing = isProcessing || hasAsyncStep || hasTimedHold;
    const canVerify = task.status === TaskStatus.VERIFICATION_NEEDED && allSynced && !pendingApproval && !workflowNeedsAttention && !hasAsyncStep && !hasTimedHold;
    needsAttention = workflowNeedsAttention || Boolean(pendingApproval) || steps.some(_temp8);
    const verifyTarget = faxedBack ? "Confirm the not-our-patient fax was sent to the sender." : signed ? "Confirm the Plan of Care entry, both attachments, and the POC Received task in Clinicient." : "Confirm the chart note and POC Received task in Clinicient.";
    let t18;
    if ($[47] !== verifyTarget) {
      t18 = <p className="text-sm">{verifyTarget} Then use Correct or Incorrect at the bottom to complete this task.</p>;
      $[47] = verifyTarget;
      $[48] = t18;
    } else {
      t18 = $[48];
    }
    steps.push({
      title: faxedBack ? "Verify the fax" : "Verify in Clinicient",
      status: isCompleted ? "verified" : hasFailedStep ? "failed" : workflowNeedsAttention || pendingApproval || canVerify ? "needs_action" : "waiting",
      summary: isCompleted ? "This task has been marked complete." : pendingApproval ? "Complete the pending workflow confirmation before verifying this task." : workflowNeedsAttention ? "Resolve the outstanding workflow issue before verifying this task." : workflowContinuing ? "The workflow is continuing. Verify the results when it finishes." : !allSynced ? "Resolve the incomplete sync steps above before verifying this task." : verifyTarget,
      content: t18
    });
    $[0] = caseRecordDescription;
    $[1] = dateOfEvent;
    $[2] = noteText;
    $[3] = pendingApproval;
    $[4] = signed;
    $[5] = stepKeys;
    $[6] = task;
    $[7] = taskFields;
    $[8] = taskSteps;
    $[9] = hasInformationNeededStep;
    $[10] = inboundNote;
    $[11] = isCompleted;
    $[12] = needsAttention;
    $[13] = steps;
    $[14] = workflowContinuing;
  } else {
    hasInformationNeededStep = $[9];
    inboundNote = $[10];
    isCompleted = $[11];
    needsAttention = $[12];
    steps = $[13];
    workflowContinuing = $[14];
  }
  const t1 = !isCompleted && taskSteps.length === 0 && !needsAttention ? "No workflow steps have been recorded for this task yet." : undefined;
  let t2;
  if ($[49] !== hasInformationNeededStep || $[50] !== inboundNote || $[51] !== needsAttention) {
    t2 = needsAttention && inboundNote && !hasInformationNeededStep ? <p className="whitespace-pre-wrap text-sm">{inboundNote}</p> : undefined;
    $[49] = hasInformationNeededStep;
    $[50] = inboundNote;
    $[51] = needsAttention;
    $[52] = t2;
  } else {
    t2 = $[52];
  }
  let t3;
  if ($[53] !== steps || $[54] !== t1 || $[55] !== t2 || $[56] !== workflowContinuing) {
    t3 = <ReferralWorkflowTimeline steps={steps} isProcessing={workflowContinuing} waitingMessage={t1} summaryContent={t2} />;
    $[53] = steps;
    $[54] = t1;
    $[55] = t2;
    $[56] = workflowContinuing;
    $[57] = t3;
  } else {
    t3 = $[57];
  }
  return t3;
}
function _temp8(step_6) {
  return step_6.status === "needs_action" || step_6.status === "failed";
}
function _temp7(step_5) {
  return step_5.status === TaskStepStatus.ASYNC;
}
function _temp6(step_4) {
  return step_4.status === TaskStepStatus.PAUSED;
}
function _temp5(step_3) {
  return step_3.status === TaskStepStatus.FAILED;
}
function _temp4(step_2) {
  return step_2.status === "automated" || step_2.status === "not_needed";
}
function _temp3(file) {
  return file.type === FileType.INBOUND;
}
function _temp2(step_0) {
  return step_0?.output?.outcome === "skipped";
}
function _temp(a, b) {
  return a.stepIndex - b.stepIndex;
}